import {DispatchEntity, transportCategory, useGetDispatchTrackingQuery} from "@ots-frontend/ots-data";
import {Alert, Card, Divider, Grid, Group, Text, Title} from "@mantine/core";
import {useTranslation} from "react-i18next";
import {DefaultText} from "../../text/DefaultText/DefaultText";
import {FormattedDateTime} from "../../date/FormattedDateTime/FormattedDateTime";
import {FormattedQuantity} from "../../text/FormattedQuantity/FormattedQuantity";
import {EntityAnchor} from "../../anchor/EntityAnchor/EntityAnchor";
import {DispatchTimeline} from "../../timeline/DispatchTimeline/DispatchTimeline";
import {DispatchSplitsCard} from "./DispatchSplitsCard";
import {DispatchTrackingCard} from "../common/DispatchTrackingCard/DispatchTrackingCard";
import {
  getDispatchTrackingPollingInterval,
  isDispatchTrackingClosedStatus
} from "../common/DispatchTrackingCard/dispatch-tracking-polling";
import {useEffect, useState} from "react";

export type DispatchDetailsProps = {
  dispatch: DispatchEntity
}

export const DispatchDetails = (props: DispatchDetailsProps) => {
  const {dispatch} = props;

  const {t} = useTranslation(["common"]);

  const [trackingPollingInterval, setTrackingPollingInterval] = useState(() =>
    getDispatchTrackingPollingInterval(dispatch.status)
  );
  const trackingClosed = isDispatchTrackingClosedStatus(dispatch.status);

  const {
    data: tracking,
    isLoading: isTrackingLoading,
    isFetching: isTrackingFetching,
    error: trackingError,
    refetch: refetchTracking
  } = useGetDispatchTrackingQuery(dispatch.id, {
    pollingInterval: trackingPollingInterval,
    skipPollingIfUnfocused: true,
    skip: trackingClosed
  });

  useEffect(() => {
    setTrackingPollingInterval(getDispatchTrackingPollingInterval(dispatch.status, tracking));
  }, [dispatch.status, tracking]);

  return (
    <Grid>
      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>

          <Group position="apart" pb="sm">
            <Title
              order={4}>{t("common.entity.dispatch.profile.info.section.details.label", "Details")}</Title>
          </Group>

          <Divider/>

          <Grid>
            {dispatch.dispatchRequest &&
              <Grid.Col span={12}>
                <Alert m="sm" title={t("common.entity.dispatch.profile.dispatchrequest", "Linked to Request")}>
                    <span>
                      {(dispatch.dispatchRequest.externalId || dispatch.dispatchRequest.externalSystem) &&
                        ((dispatch.dispatchRequest?.externalId && dispatch.dispatchRequest?.externalSystem ? (`${dispatch.dispatchRequest.externalId} (${dispatch.dispatchRequest.externalSystem})`) : dispatch.dispatchRequest?.externalId) ?? "")
                      }
                      <EntityAnchor value={dispatch.dispatchRequest}/>
                    </span>
                </Alert>
              </Grid.Col>
            }
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.dispatch.profile.info.field.scheduleddate.label", "Scheduled Date")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedDateTime date={dispatch.scheduleDttm}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.dispatch.profile.info.field.driver.label", "Driver")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                {dispatch.personId ? (
                  <EntityAnchor
                    value={{
                      objectId: dispatch.personId,
                      objectName: dispatch.personName ?? t<string>("common.shared.unspecified", "Unspecified"),
                      objectType: 'PERSON'
                    }}/>
                ) : (
                  <>
                    {t("common.shared.unspecified", "Unspecified")}
                  </>
                )}
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.dispatch.profile.info.field.quantity.label", "Quantity")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <FormattedQuantity value={{
                  number: dispatch.quantity,
                  units: dispatch.quantityUom
                }}/>
              </Text>
            </Grid.Col>
            <Grid.Col span={6}>
              <Text ta="left" fz="sm" weight={500} mt="md">
                {t("common.entity.dispatch.profile.info.field.freighttype.label", "Freight Type")}
              </Text>
              <Text ta="left" c="dimmed" fz="xs">
                <DefaultText value={dispatch.freightTypeName}/>
              </Text>
            </Grid.Col>
            {(dispatch.category === transportCategory.water || dispatch.pickupServiceTypeId) &&
              <Grid.Col span={12}>
                <Text ta="left" fz="sm" weight={500} mt="md">
                  {t("common.entity.dispatch.profile.info.field.servicetype.label", "Service Type")}
                </Text>
                <Text ta="left" c="dimmed" fz="xs">
                  {dispatch.pickupServiceTypeId ?
                    <DefaultText value={dispatch.pickupServiceTypeName}/> : undefined}
                </Text>
              </Grid.Col>
            }
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Grid>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.pickupsite.label", "Pickup Site")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.pickupSiteId,
                        objectName: dispatch.pickupSiteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'SITE'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.pickupwell.label", "Pickup Well")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.pickupWellId,
                        objectName: dispatch.pickupWellName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'WELL'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.pickupcontainer.label", "Pickup Container")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.pickupContainerId,
                        objectName: dispatch.pickupContainerName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'CONTAINER'
                      }}
                    />
                  </Text>
                </Grid.Col>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.deliverysite.label", "Delivery Site")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.deliverySiteId,
                        objectName: dispatch.deliverySiteName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'SITE'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.deliverywell.label", "Delivery Well")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.deliveryWellId,
                        objectName: dispatch.deliveryWellName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'WELL'
                      }}
                    />
                  </Text>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.deliverycontainer.label", "Delivery Container")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <EntityAnchor
                      value={{
                        objectId: dispatch.deliveryContainerId,
                        objectName: dispatch.deliveryContainerName ?? t<string>("common.shared.unspecified", "Unspecified"),
                        objectType: 'CONTAINER'
                      }}
                    />
                  </Text>
                </Grid.Col>
              </Grid>
            </Grid.Col>
            <Grid.Col span={12}>
              <Divider variant={"dashed"}/>
            </Grid.Col>
            <Grid.Col span={12}>
              <Grid>
                <Grid.Col span={6}>
                  <Text ta="left" fz="sm" weight={500} mt="md">
                    {t("common.entity.dispatch.profile.info.field.workordernumber.label", "Work Order Number")}
                  </Text>
                  <Text ta="left" c="dimmed" fz="xs">
                    <DefaultText value={dispatch.workOrderNumber}
                                 defaultValue={t<string>("common.shared.unspecified", "Unspecified")}/>
                  </Text>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      <Grid.Col span={6}>
        <Card shadow="sm" padding="lg" radius="md" withBorder>
          <Group position="apart" pb="sm">
            <Title order={4}>{t("profile.dispatch.info.section.timeline.label", "Timeline")}</Title>
          </Group>
          <Divider/>
          <Grid>
            <Grid.Col span={12}>
              <DispatchTimeline
                m="md"
                bulletSize={24}
                lineWidth={2}
                dispatch={dispatch}/>
            </Grid.Col>
          </Grid>
        </Card>
      </Grid.Col>

      {!trackingClosed &&
        <Grid.Col span={12}>
          <DispatchTrackingCard
            tracking={tracking}
            loading={isTrackingLoading}
            fetching={isTrackingFetching}
            error={trackingError}
            refreshInterval={trackingPollingInterval}
            onRefresh={refetchTracking}/>
        </Grid.Col>
      }

      <Grid.Col span={12}>
        <DispatchSplitsCard dispatch={dispatch}/>
      </Grid.Col>

    </Grid>
  );
};

